import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import MainViewHeader from '../components/MainViewHeader';
import { useInstance } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation';
import API_ENDPOINTS from '../config/api';
import SocketService from '../../../../packages/shared/socket';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  overflow: hidden;
`;

const ContentArea = styled.div`
  padding-top: 64px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 16px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const MoodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  padding: 32px;
  max-width: 1200px;
  width: 100%;
`;

const MoodCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
  }
`;

const MoodEmoji = styled.div`
  font-size: 3rem;
  margin-bottom: 12px;
`;

const MoodLabel = styled.div`
  color: white;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const MoodCount = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 2rem;
  font-weight: 700;
`;

const MoodPercentage = styled.div`
  color: ${props => props.$color || 'rgba(255, 255, 255, 0.5)'};
  font-size: 0.9rem;
  margin-top: 4px;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
  margin: 0;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  min-height: 44px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
`;

// Mood configuration; labels are moodScreen.moods.<mood>
const MOOD_CONFIG = {
  positive: { emoji: '😊', color: '#10B981' },
  neutral: { emoji: '😐', color: '#F59E0B' },
  negative: { emoji: '😟', color: '#EF4444' },
  hopeful: { emoji: '🌟', color: '#3B82F6' },
  concerned: { emoji: '😰', color: '#F97316' }
};

/**
 * MoodScreen - Displays mood/sentiment distribution from workshop submissions.
 * Shows only what was fetched: no instance, a failed fetch and no submissions
 * each say so (#1413).
 */
function MoodScreen() {
  const [searchParams] = useSearchParams();
  const { currentInstance } = useInstance();
  const { t } = useTranslation();
  const [moodData, setMoodData] = useState({});
  const [loading, setLoading] = useState(true);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  // null | { key: 'moodScreen.noInstance' } | { key: 'moodScreen.loadFailed', reason }
  const [problem, setProblem] = useState(null);

  // Connect to socket
  useEffect(() => {
    const socketUrl = API_ENDPOINTS.SOCKET_URL();
    SocketService.connect(socketUrl, {
      onConnect: () => console.log('[MoodScreen] Connected to socket'),
      onDisconnect: () => console.log('[MoodScreen] Disconnected from socket')
    });
  }, []);

  // Fetch mood data
  const fetchMoodData = useCallback(async () => {
    setLoading(true);
    setProblem(null);
    setMoodData({});
    setTotalSubmissions(0);
    const instanceId = searchParams.get('kid') || searchParams.get('instance') || searchParams.get('instanceId') || currentInstance?.id;

    if (!instanceId) {
      setProblem({ key: 'moodScreen.noInstance' });
      setLoading(false);
      return;
    }

    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const response = await fetch(`${socketUrl}/api/pathways?kid=${instanceId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const pathways = data.pathways || data || [];

      // Count moods from submissions
      const moodCounts = {};
      pathways.forEach(p => {
        if (p.mood && !p.is_scenario_starter) {
          moodCounts[p.mood] = (moodCounts[p.mood] || 0) + 1;
        }
      });

      // If no moods found, use neutral for all
      const total = Object.values(moodCounts).reduce((a, b) => a + b, 0);
      if (total === 0) {
        const nonStarterCount = pathways.filter(p => !p.is_scenario_starter).length;
        moodCounts.neutral = nonStarterCount;
      }

      setMoodData(moodCounts);
      setTotalSubmissions(Object.values(moodCounts).reduce((a, b) => a + b, 0));
    } catch (err) {
      console.error('Error fetching mood data:', err);
      setProblem({ key: 'moodScreen.loadFailed', reason: err.message });
    } finally {
      setLoading(false);
    }
  }, [searchParams, currentInstance]);

  useEffect(() => {
    fetchMoodData();
  }, [fetchMoodData]);

  // Sort moods by count
  const sortedMoods = useMemo(() => {
    return Object.entries(moodData)
      .sort(([, a], [, b]) => b - a)
      .map(([mood, count]) => ({
        mood,
        count,
        config: MOOD_CONFIG[mood] || { emoji: '❓', color: '#666' },
        label: MOOD_CONFIG[mood] ? t(`moodScreen.moods.${mood}`) : mood,
        percentage: totalSubmissions > 0 ? Math.round((count / totalSubmissions) * 100) : 0
      }));
  }, [moodData, totalSubmissions, t]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner />
          <p style={{ color: 'white' }}>{t('moodScreen.loading')}</p>
        </LoadingContainer>
      </Container>
    );
  }

  const header = (
    <MainViewHeader
      activeView="mood"
      instanceTitle={currentInstance?.title || ''}
    />
  );

  if (problem || totalSubmissions === 0) {
    return (
      <Container>
        {header}
        <ContentArea>
          <Title>{t('moodScreen.title')}</Title>
          <Subtitle role={problem?.key === 'moodScreen.loadFailed' ? 'alert' : 'status'}>
            {t(problem?.key || 'moodScreen.empty', { reason: problem?.reason })}
          </Subtitle>
          {problem?.key === 'moodScreen.loadFailed' && (
            <RetryButton type="button" onClick={fetchMoodData}>{t('moodScreen.retry')}</RetryButton>
          )}
        </ContentArea>
      </Container>
    );
  }

  return (
    <Container>
      {header}
      <ContentArea>
        <div>
          <Title>{t('moodScreen.title')}</Title>
          <Subtitle>{t('moodScreen.analysed', { count: totalSubmissions })}</Subtitle>
        </div>

        <MoodGrid>
          {sortedMoods.map(({ mood, count, config, label, percentage }) => (
            <MoodCard key={mood}>
              <MoodEmoji>{config.emoji}</MoodEmoji>
              <MoodLabel>{label}</MoodLabel>
              <MoodCount>{count}</MoodCount>
              <MoodPercentage $color={config.color}>
                {percentage}%
              </MoodPercentage>
            </MoodCard>
          ))}
        </MoodGrid>
      </ContentArea>
    </Container>
  );
}

export default MoodScreen;
